import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import {
  FailureMessage,
  FieldList,
  Modal,
  TextFieldInput,
} from "@smart/itops-sb-design-system-dom";
import { buildLogProps } from "@smart/itops-utils-basic";
import { Gql } from "@smart/manage-gql-operations-dom";

import { ContactSearchField } from "../../shared/contact-search-field";

export type TabAddContactProps = {
  onClose: () => void;
  onCreate: (props: {
    firstName: string;
    lastName: string;
    email: string;
    id?: string;
  }) => Promise<void>;
  visible: boolean;
};

const tabAddContactSchema = z.object({
  firstName: z.string().min(1, "Please enter a first name"),
  lastName: z.string().min(1, "Please enter a last name"),
  email: z
    .string()
    .min(1, "Please enter an email")
    .email("Please enter a valid email"),
  selectedContact: z
    .object({
      person: z
        .object({
          firstName: z.string().optional().nullable(),
          lastName: z.string().optional().nullable(),
          email: z.string().optional().nullable(),
        })
        .passthrough(),
    })
    .passthrough()
    .optional()
    .nullable(),
});

export const TabAddContact = ({
  onClose,
  onCreate,
  visible,
}: TabAddContactProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState<string>();

  const form = useForm<{
    firstName: string;
    lastName: string;
    email: string;
    selectedContact: Gql.ContactFieldsFragment | null;
  }>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      selectedContact: null,
    },
    resolver: zodResolver(tabAddContactSchema),
  });
  const { handleSubmit, watch, setValue, control, trigger } = form;

  const onSubmit = handleSubmit(
    async ({ firstName, lastName, email, selectedContact }) => {
      setHasError(undefined);
      setIsLoading(true);
      try {
        await onCreate({
          firstName: selectedContact?.person?.firstName || firstName,
          lastName: selectedContact?.person?.lastName || lastName,
          email,
          id: selectedContact?.id,
        });
      } catch (e) {
        const { logMessage, errorProps } = buildLogProps(e);
        setHasError(`${logMessage} - ${errorProps.message}`);
      }
      setIsLoading(false);
    },
  );

  const selectedContact = watch("selectedContact");

  useEffect(() => {
    const person = selectedContact?.person;
    if (person) {
      setValue("email", person.email || "");
      if (person.email) trigger(["email"]).catch(console.error);
      setValue("firstName", person.firstName || "");
      if (person.firstName) trigger(["firstName"]).catch(console.error);
      setValue("lastName", person.lastName || "");
      if (person.lastName) trigger(["lastName"]).catch(console.error);
    }
  }, [selectedContact]);

  return (
    <Modal
      header={{
        icon: "solidContactCard",
        text: "Missing contact for the matter",
      }}
      open={visible}
      onClose={onClose}
      footer={{
        buttons: [
          {
            text: "Cancel",
            type: "reset",
            variant: "secondarySubtle",
            onClick: onClose,
          },
          {
            text: "Save To Matter",
            onClick: onSubmit,
          },
        ],
      }}
      loading={isLoading}
      closeOptions={{ clickOutside: false }}
    >
      <p>Enter client contact details in order to share a form to them.</p>
      <FieldList>
        <Controller
          control={control}
          name="firstName"
          render={({ field, fieldState }) => (
            <ContactSearchField
              contactType="person"
              id={field.name}
              errorId={`${field.name}-error`}
              dataTestId="add-contact-first-name"
              field={{
                uri: field.name,
                sectionUri: "",
                order: "a",
                type: "text",
                label: "First Name",
                mandatory: true,
              }}
              value={
                selectedContact
                  ? selectedContact.person?.firstName || ""
                  : field.value
              }
              error={fieldState.error?.message}
              loading={false}
              disabled={!!selectedContact}
              onChange={(val) => {
                if (!val && selectedContact) setValue("selectedContact", null);
                field.onChange(val);
              }}
              onBlur={field.onBlur}
              onSelect={(contact) => {
                setValue("selectedContact", contact);
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="lastName"
          render={({ field, fieldState }) => (
            <ContactSearchField
              contactType="person"
              id={field.name}
              errorId={`${field.name}-error`}
              dataTestId="add-contact-last-name"
              field={{
                uri: field.name,
                sectionUri: "",
                order: "b",
                type: "text",
                label: "Last Name",
                mandatory: true,
              }}
              value={
                selectedContact
                  ? selectedContact.person?.lastName || ""
                  : field.value
              }
              error={fieldState.error?.message}
              loading={false}
              disabled={!!selectedContact}
              onChange={(val) => {
                if (!val && selectedContact) setValue("selectedContact", null);
                field.onChange(val);
              }}
              onBlur={field.onBlur}
              onSelect={(contact) => {
                setValue("selectedContact", contact);
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="email"
          render={({ field, fieldState }) => (
            <TextFieldInput
              id={field.name}
              label="Email"
              message={fieldState.error?.message}
              error={!!fieldState.error}
              dataTestId="add-contact"
              mandatory
              {...field}
            />
          )}
        />
      </FieldList>
      {hasError && (
        <FailureMessage action="save contact information" title={hasError} />
      )}
    </Modal>
  );
};
